import { Badge, EmptyState, Skeleton } from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { Building2 } from 'lucide-react';
import { LoadFailed } from '../LoadFailed.tsx';
import { usePageContext } from '../usePageContext.ts';
import styles from './Rows.module.css';
import { SETTINGS_SECTIONS } from './settings-sections.ts';
import { SettingsSection } from './SettingsSection.tsx';
import { useSubsidiaries, type Subsidiary } from './subsidiaries.ts';

const detailsOf = (subsidiary: Subsidiary): string =>
  [subsidiary.taxId, subsidiary.phone, subsidiary.address].filter((part) => part !== null).join(' · ');

export const SubsidiariesSection = ({ api }: { api: TypedClient }) => {
  const { t, describeError } = usePageContext();
  const subsidiaries = useSubsidiaries(api);

  const body = () => {
    if (subsidiaries.isPending) return <Skeleton height="64px" label={t('subsidiaries.title')} />;
    if (subsidiaries.isError)
      return (
        <LoadFailed
          description={describeError(subsidiaries.error)}
          onRetry={() => {
            void subsidiaries.refetch();
          }}
        />
      );
    if (subsidiaries.data.length === 0)
      return <EmptyState title={t('subsidiaries.none')} description={t('subsidiaries.noneHelp')} />;
    return (
      <ul className={styles['rows']}>
        {subsidiaries.data.map((subsidiary) => (
          <li
            key={subsidiary.id}
            className={[styles['row'], subsidiary.archived ? styles['archived'] : ''].join(' ')}
          >
            <span className={styles['mark']} aria-hidden="true">
              {subsidiary.name.charAt(0).toUpperCase()}
            </span>
            <span className={styles['text']}>
              <span className={styles['title']}>
                {subsidiary.name}
                {subsidiary.archived ? <Badge tone="neutral">{t('subsidiaries.archived')}</Badge> : null}
              </span>
              <span className={styles['meta']}>{detailsOf(subsidiary) || t('subsidiaries.noDetails')}</span>
            </span>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <SettingsSection
      id={SETTINGS_SECTIONS.subsidiaries}
      icon={Building2}
      title={t('subsidiaries.title')}
      description={t('subsidiaries.context')}
    >
      {body()}
    </SettingsSection>
  );
};
